import React, { FC } from "react";
import styled from "styled-components";

import type {
  ClientPlatform,
  RaceTapPath,
  RaceTimingMargins,
  RaceTimingRacer,
} from "./types";
import { Pill, Row, Table, TableScroll, colors } from "./styles";

// Timing quality (aotdrace-2.x): the per-racer table and the race margins in
// the Race stats tool, plus the small formatters the per-day race table shares.

/** A device clock further off than this is flagged. */
export const CLOCK_WARN_MS = 300;

const SubNote = styled.div`
  font-size: 0.72rem;
  color: ${colors.textMuted};
  margin-top: 2px;
  white-space: nowrap;
`;

const Caption = styled.div`
  margin-top: 12px;
  color: ${colors.textMuted};
  font-size: 0.85rem;
`;

const nowrap: React.CSSProperties = { whiteSpace: "nowrap" };
const muted: React.CSSProperties = { color: colors.textMuted };

export function platformLabel(p?: ClientPlatform | null): string {
  switch (p) {
    case "android":
      return "Android";
    case "ios":
      return "iOS";
    case "macos":
      // iPads send the Mac User-Agent, so they land here too.
      return "Mac / iPad";
    case "windows":
      return "Windows";
    case "linux":
      return "Linux";
    case "other":
      return "other";
    default:
      return "—";
  }
}

export function tapPathLabel(t?: RaceTapPath | null): string {
  if (t === "auto") return "page load";
  return t || "—";
}

function fmtSize(absMs: number): string {
  return absMs < 1000 ? `${absMs}ms` : `${(absMs / 1000).toFixed(2)}s`;
}

/** Signed size, e.g. "+1.30s" or "-80ms". */
function fmtSigned(ms: number): string {
  const r = Math.round(ms);
  return `${r < 0 ? "-" : "+"}${fmtSize(Math.abs(r))}`;
}

/** "+1.30s ahead", "-80ms behind", "in sync". */
export function fmtClockAhead(ms: number): string {
  const r = Math.round(ms);
  if (r === 0) return "in sync";
  return `${fmtSigned(r)} ${r > 0 ? "ahead" : "behind"}`;
}

/** Device clock vs server: muted dash when unknown, warn pill when far off. */
export const ClockAhead: FC<{ ms?: number | null }> = ({ ms }) => {
  if (ms == null) return <span style={muted}>—</span>;
  if (Math.abs(ms) > CLOCK_WARN_MS) {
    return (
      <Pill $tone="warn" style={nowrap}>
        {fmtClockAhead(ms)}
      </Pill>
    );
  }
  return <span style={nowrap}>{fmtClockAhead(ms)}</span>;
};

function fmtSeconds(ms: number | null): string {
  return ms == null ? "—" : `+${(ms / 1000).toFixed(2)}s`;
}

function fmtMs(ms: number | null): string {
  return ms == null ? "—" : `${ms}ms`;
}

function pct(share: number): string {
  return `${Math.round(share * 100)}%`;
}

function correctedTone(share: number): "ok" | "warn" | "err" {
  if (share >= 0.8) return "ok";
  if (share >= 0.5) return "warn";
  return "err";
}

/** One row per racer, most races first. */
export const TimingQualityTable: FC<{ racers: RaceTimingRacer[] }> = ({ racers }) => {
  const rows = [...racers].sort((a, b) => b.races - a.races || b.wins - a.wins);
  return (
    <TableScroll style={{ marginTop: 6 }}>
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>User</th>
            <th>Device</th>
            <th>Races</th>
            <th>Wins</th>
            <th>Reaction</th>
            <th>Best</th>
            <th>Corrected</th>
            <th>1-way lat</th>
            <th>Jitter</th>
            <th>Clock</th>
            <th>Page load</th>
            <th>Early</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.userId}>
              <td>
                <strong>{i + 1}</strong>
              </td>
              <td style={nowrap}>
                {r.userEmoji ? `${r.userEmoji} ` : ""}
                {r.username}
              </td>
              <td style={nowrap}>
                {platformLabel(r.platform)}
                {r.platformSource === "session" && <SubNote>from sign-in</SubNote>}
              </td>
              <td>{r.races}</td>
              <td>{r.wins}</td>
              <td>{fmtSeconds(r.reactionMedianMs)}</td>
              <td style={muted}>{fmtSeconds(r.reactionBestMs)}</td>
              <td>
                {r.correctedShare == null ? (
                  "—"
                ) : (
                  <Pill $tone={correctedTone(r.correctedShare)}>{pct(r.correctedShare)}</Pill>
                )}
              </td>
              <td>{fmtMs(r.latencyMedianMs)}</td>
              <td>{fmtMs(r.jitterMedianMs)}</td>
              <td style={nowrap}>
                <ClockAhead ms={r.clockAheadMedianMs} />
                {r.clockSamples > 1 &&
                  r.clockAheadP10Ms != null &&
                  r.clockAheadP90Ms != null && (
                    <SubNote>
                      {fmtSigned(r.clockAheadP10Ms)} to {fmtSigned(r.clockAheadP90Ms)} ·{" "}
                      {r.clockSamples} taps
                    </SubNote>
                  )}
              </td>
              <td>
                {r.pageLoadShare == null ? (
                  "—"
                ) : r.pageLoadShare >= 0.25 ? (
                  <Pill $tone="warn">{pct(r.pageLoadShare)}</Pill>
                ) : (
                  <span style={r.pageLoadShare === 0 ? muted : undefined}>
                    {pct(r.pageLoadShare)}
                  </span>
                )}
              </td>
              <td style={r.earlyTaps ? undefined : muted}>{r.earlyTaps}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

/** Gap between 1st and 2nd contest time on contested days, plus the closest five. */
export const RaceMarginsSummary: FC<{ margins: RaceTimingMargins }> = ({ margins }) => (
  <>
    <Row style={{ marginTop: 6 }}>
      <Pill $tone="info">
        median gap {margins.medianMs == null ? "—" : fmtSize(margins.medianMs)}
      </Pill>
      {margins.p10Ms != null && (
        <Pill $tone="muted">1 in 10 closer than {fmtSize(margins.p10Ms)}</Pill>
      )}
      <Pill $tone={margins.under50 > 0 ? "warn" : "muted"}>
        under 50ms: {margins.under50} of {margins.count}
      </Pill>
      <Pill $tone="muted">
        under 150ms: {margins.under150} of {margins.count}
      </Pill>
    </Row>
    {margins.closest.length > 0 && (
      <>
        <Caption>Closest races</Caption>
        <TableScroll style={{ marginTop: 6 }}>
          <Table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Winner</th>
                <th>Runner-up</th>
                <th>Gap</th>
              </tr>
            </thead>
            <tbody>
              {margins.closest.map((m) => (
                <tr key={m.dayKey}>
                  <td style={nowrap}>{m.dayKey}</td>
                  <td>{m.winner}</td>
                  <td>{m.runnerUp}</td>
                  <td>
                    <Pill $tone={m.marginMs < 50 ? "warn" : "muted"}>{fmtSize(m.marginMs)}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </>
    )}
  </>
);
